import AppButton from "../../../components/ui/AppButton";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import { formatCurrency } from "../../../lib/utils";
import CrewDropdownMenu from "../../driver/ui/CrewDropDownMenu";
import DriverDropdownMenu from "../../driver/ui/DriverDropdownMenu";
import { FuelExpense } from "../@types";

interface CrewMemberInput {
  crewType: string;
  id: string;
}

interface OtherExpense {
  description: string;
  amount: number;
}

interface IncomeInput {
  description: string;
  expectedAmount: number;
  earnedAmount: number;
}

interface FormData {
  fromLocation: string;
  toLocation: string;
  approximateDistance: number;
  driverId: string;
  vehicleId: string;
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  crewMembers: CrewMemberInput[];
  fuelExpenses: FuelExpense;
  driverId: string;
  otherExpenses: OtherExpense[];
  incomes: IncomeInput[];
  onProceed: () => void;
  isCreating: boolean;
}

const ReviewModal = ({
  isOpen,
  onClose,
  formData,
  crewMembers,
  fuelExpenses,
  driverId,
  otherExpenses,
  incomes,
  onProceed,
  isCreating,
}: ReviewModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm Dispatch"
      className="max-w-3xl p-2"
    >
      <div className="p-10">
        <div className="border border-gray-300 rounded-[10px] items-center justify-center p-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Trip Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-[13px] pb-2">Trip Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Start Location</p>
                <div className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] pl-4 text-[13px] font-normal flex items-center rounded-[10px]">
                  {formData.fromLocation}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">End Location</p>
                <div className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] pl-4 text-[13px] font-normal flex items-center rounded-[10px]">
                  {formData.toLocation}
                </div>
              </div>
              {/* <div>
              <p className="text-sm text-gray-600 mb-2">Expected Time</p>
              <p className="font-medium">{formData.estimatedTime} minutes</p>
            </div> */}
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Exp. Distance covered
                </p>
                <div className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] pl-4 text-[13px] font-normal flex items-center rounded-[10px]">
                  {formData.approximateDistance} km
                </div>
              </div>
            </div>
          </div>

          {/* Crew Members */}
          {crewMembers.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-[13px] pb-2">Crew Members</h3>

              <div className="">
                <DriverDropdownMenu
                  value={driverId}
                  onChange={(value) => {}}
                  disabled
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                />
              </div>
              <div className="space-y-2">
                {crewMembers.length > 0 &&
                  crewMembers.map((crew, index) => (
                    <div key={index} className="text-sm bg-gray-50 p-3 rounded">
                      <span className="font-medium">{crew.crewType}:</span>
                      <CrewDropdownMenu
                        value={crew.id}
                        onChange={(value) => {}}
                        showLabel={false}
                        className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full text-gray-800"
                        placeholder="Select crew member"
                        disabled
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Expenses */}
          {(fuelExpenses || otherExpenses.length > 0) && (
            <div className="space-y-4">
              <h3 className="font-semibold text-[13px] border-b pb-2">
                Expenses
              </h3>
              <div className="space-y-2">
                {fuelExpenses && fuelExpenses.amount && (
                  <div className="text-sm bg-gray-50 p-3 rounded">
                    <span className="font-medium">Fuel Purchase:</span>{" "}
                    {fuelExpenses.quantity} liters -{" "}
                    {formatCurrency(fuelExpenses.amount)}
                  </div>
                )}
                {otherExpenses.map((exp, index) => (
                  <div key={index} className="text-sm bg-gray-50 p-3 rounded">
                    <span className="font-medium">{exp.description}:</span>
                    {formatCurrency(exp.amount)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Income */}
          {incomes.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-[13px] border-b pb-2">
                Income
              </h3>
              <div className="space-y-2">
                {incomes.map((inc, index) => (
                  <div key={index} className="text-sm bg-gray-50 p-3 rounded">
                    <div className="font-medium mb-1">{inc.description}</div>
                    <div className="text-gray-600">
                      Expected: {formatCurrency(inc.expectedAmount)} | Earned:
                      {formatCurrency(inc.earnedAmount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center items-center space-x-4 pt-4 border-t">
            <AppButton
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="button"
              onClick={onProceed}
              disabled={isCreating}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px] disabled:opacity-50"
            >
              {isCreating ? <Spinner /> : "Proceed"}
            </AppButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReviewModal;
